import { FormikConfig, FormikProps, FormikValues, useFormik as useFormikLib } from 'formik'
import { FieldInputProps } from 'formik/dist/types'
import { useState } from 'react'

import './formik.hook.d'

export type Formik<T extends FormikValues = FormikValues> = Omit<FormikProps<T>, 'getFieldProps'> & {
  getFieldProps: <Field extends keyof T & string = keyof T & string>(
    field: Field,
    onChangeType?: 'event' | 'value',
  ) => FieldInputProps<T[Field]>
}

interface UserFormikProps<Values> extends FormikConfig<Values> {
  isValidatedOnBlur?: boolean
  isValidatedOnChange?: boolean
}

/**
 * Manipulating formik config and state hook.
 * Used to predefine common props and no-validate-before-submit logics.
 * @param isValidatedOnBlur configures if validation should perform on blur (without submit attempt)
 * @param isValidatedOnChange configures if validation should perform on change (without submit attempt)
 * @param props formik config, e.g. initials, validation, handlers
 * @returns formik state object
 */
export const useFormik = <Values extends FormikValues = FormikValues>({
  isValidatedOnBlur = false,
  isValidatedOnChange = false,
  ...props
}: UserFormikProps<Values>): Formik<Values> => {
  const [isValidationTriggered, setIsValidationTriggered] = useState(false)

  const handleValidate = () => {
    if (!isValidationTriggered) {
      setIsValidationTriggered(true)
    }
  }

  const onReset = () => setIsValidationTriggered(false)

  const state = useFormikLib({
    ...props,
    validateOnBlur: isValidationTriggered || isValidatedOnBlur,
    validateOnChange: isValidationTriggered || isValidatedOnChange,
    validate: handleValidate,
    onReset: (...args) => {
      props.onReset?.(...args)
      onReset()
    },
  }) as Formik<Values>

  const getFieldProps: Formik<Values>['getFieldProps'] = (key, type: 'event' | 'value' = 'event') => {
    switch (type) {
      case 'value':
        return {
          ...state.getFieldProps(key),
          onChange: (value: unknown) => state.handleChange({ type: 'change', target: { value, name: key } }),
          error: state.errors[key],
        }
      case 'event':
      default:
        return {
          ...state.getFieldProps(key),
          onChange: (value: unknown) => {
            if (!(value instanceof Event)) {
              console.warn(
                `Use type "value" instead of "event" as second parameter for "getFieldProps" method for correct working!(field: "${key}")`,
              )
            }

            state.handleChange(value)
          },
          error: state.errors[key],
        }
    }
  }

  return { ...state, getFieldProps }
}

export { useFormikContext } from 'formik'
